import type { ProfilePage } from "@amber/application/contracts";
import { EDITOR_IDENTITY_REASON, EDITOR_PROFILES_REASON, type PlanView } from "@amber/profiles/contracts";
import { formatDate } from "@amber/ui";

export type ProfilePanel = "start" | "review" | "plan" | "pause" | "identity" | "refresh" | "merge" | "split";

function startReasonOf(editor: boolean, discovering: boolean, started: boolean): string | null {
  if (!editor) return EDITOR_PROFILES_REASON;
  if (discovering) return "Source discovery is still drafting the first plan";
  if (started) return "Tracking has started and is waiting for the scheduler";
  return null;
}

function planLineOf(p: ProfilePage, plan: PlanView | null, started: boolean): string {
  if (p.lifecycle_state === "archived")
    return `Retired${p.retired ? ` ${formatDate(p.retired.at)}` : ""}: not monitored`;
  if (plan?.applied)
    return `Plan applied ${formatDate(plan.applied.applied_at ?? plan.applied.recorded_at)} · next review ${formatDate(plan.applied.review_date)}`;
  if (started) return "Tracking started: waiting for the scheduler";
  if (p.lifecycle_state === "incidental") return "Not tracked: no monitoring plan yet";
  return "Draft: no monitoring plan yet";
}

function editReasonOf(editor: boolean, retired: boolean): string | null {
  if (!editor) return EDITOR_IDENTITY_REASON;
  if (retired) return "Retired profiles are read-only. Reactivate it first";
  return null;
}

export function profilePageState(p: ProfilePage, plan: PlanView | null, editor: boolean) {
  const first = p.first_plan;
  const discovering = first?.discovery.state === "queued" || first?.discovery.state === "finding";
  const draftLike = p.lifecycle_state === "draft" || p.lifecycle_state === "incidental";
  const started = draftLike && plan?.pending === true;
  const retired = p.lifecycle_state === "archived";
  const editReason = editReasonOf(editor, retired);
  const references = p.references
    ? [
        p.references.items > 0 ? `${String(p.references.items)} items` : null,
        p.references.attributions > 0 ? `${String(p.references.attributions)} attributions` : null,
        p.references.sources > 0 ? `${String(p.references.sources)} sources` : null,
        p.references.plan ? "a plan" : null,
      ].filter(Boolean)
    : [];
  return {
    first,
    discovering,
    draftLike,
    started,
    retired,
    startReason: startReasonOf(editor, discovering, started),
    planLine: planLineOf(p, plan, started),
    editReason,
    deleteReason:
      editReason ?? (references.length > 0 ? `Referenced by ${references.join(", ")}. Retire it instead` : null),
  };
}

export type ProfilePageState = ReturnType<typeof profilePageState>;
